// apps/client/src/pages/admin/dashboard/roles/page.tsx

import { useState, useEffect } from "react";
import { useDialog } from "@/client/stores/dialog";
import {
  useRoles,
  useDeleteRole,
  useUpdateRole,
  useCreateRole,
  useAssignPermissions,
  Role,
} from "@/client/services/role/role";
import { usePermissions } from "@/client/services/permissions/permissions";
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ScrollArea,
  Switch,
  Input,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldIcon,
  UsersIcon,
  LockIcon,
  MagnifyingGlassIcon,
  SpinnerIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

// Schéma de validation du formulaire de rôle
const roleFormSchema = z.object({
  name: z.string().min(3, "Le nom doit contenir au moins 3 caractères").max(100),
  description: z.string().optional().nullable(),
});

type RoleFormValues = z.infer<typeof roleFormSchema>;

export const RolePermissionAdminPage = () => {
  // États locaux
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterModule, setFilterModule] = useState<string>("all");

  // État local pour les permissions du rôle sélectionné (optimiste)
  const [localPermissions, setLocalPermissions] = useState<string[]>([]);
  const [isToggling, setIsToggling] = useState<string | null>(null);

  // Dialogues
  const roleDialog = useDialog<{ roleId?: string }>("role-form");
  const permissionDialog = useDialog<{ roleId: string }>("permission-form");

  // Données
  const { data: roles, isLoading: rolesLoading, refetch: refetchRoles } = useRoles();
  const { data: permissions, isLoading: permissionsLoading } = usePermissions();
  const { mutate: deleteRole, isPending: deleting } = useDeleteRole();
  const { mutate: createRole, isPending: creating } = useCreateRole();
  const { mutate: updateRole, isPending: updating } = useUpdateRole();
  const { mutate: assignPermissions, isPending: assigning } = useAssignPermissions();

  // Rôle sélectionné
  const selectedRole = roles?.find((r) => r.id === selectedRoleId);

  // Synchroniser l'état local avec les données du serveur
  useEffect(() => {
    if (selectedRole) {
      const permIds = selectedRole.permissions?.map((p) => p.id) ?? [];
      setLocalPermissions(permIds);
    }
  }, [selectedRole]);

  // Vérifier si une permission est assignée (basé sur l'état local)
  const isPermissionAssigned = (permissionId: string) => {
    return localPermissions.includes(permissionId);
  };

  // Toggle permission (ajout/suppression) avec mise à jour optimiste
  const handleTogglePermission = (permissionId: string, currentlyAssigned: boolean) => {
    if (!selectedRoleId || !selectedRole) return;
    if (selectedRole.isSystem) return;
    if (isToggling) return;

    // Calculer le nouvel ensemble de permissions
    const newPermissions = currentlyAssigned
      ? localPermissions.filter((id) => id !== permissionId)
      : [...localPermissions, permissionId];

    // Mise à jour optimiste de l'état local
    setLocalPermissions(newPermissions);
    setIsToggling(permissionId);

    // Appel API pour mettre à jour le rôle
    assignPermissions(
      { roleId: selectedRoleId, data: { permissionIds: newPermissions } },
      {
        onSuccess: () => {
          toast.success(currentlyAssigned ? "Permission retirée" : "Permission ajoutée");
          setIsToggling(null);
          // Rafraîchir les données du rôle pour être sûr
          refetchRoles();
        },
        onError: (error: any) => {
          // En cas d'erreur, restaurer l'état précédent
          const previousPerms = selectedRole.permissions?.map((p) => p.id) ?? [];
          setLocalPermissions(previousPerms);
          setIsToggling(null);
          toast.error(error?.response?.data?.message || "Erreur lors de la modification");
        },
      },
    );
  };

  // Supprimer un rôle
  const handleDeleteRole = (id: string, name: string) => {
    if (confirm(`Voulez-vous vraiment supprimer le rôle "${name}" ?`)) {
      deleteRole(id, {
        onSuccess: () => {
          toast.success(`Rôle "${name}" supprimé avec succès`);
          if (selectedRoleId === id) setSelectedRoleId(null);
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Erreur lors de la suppression");
        },
      });
    }
  };

  // Soumission du formulaire de rôle
  const handleRoleSubmit = (data: RoleFormValues, mode: "create" | "update") => {
    const payload = {
      name: data.name,
      description: data.description || undefined,
    };

    if (mode === "create") {
      createRole(payload, {
        onSuccess: () => {
          toast.success(`Rôle "${data.name}" créé avec succès`);
          roleDialog.close();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Erreur lors de la création");
        },
      });
    } else if (selectedRoleId) {
      updateRole(
        { id: selectedRoleId, data: payload },
        {
          onSuccess: () => {
            toast.success(`Rôle "${data.name}" mis à jour avec succès`);
            roleDialog.close();
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || "Erreur lors de la mise à jour");
          },
        },
      );
    }
  };

  // Liste des modules uniques pour le filtre
  const modules = permissions
    ? Array.from(new Set(permissions.map((p) => p.module).filter(Boolean)))
    : [];

  // Permissions filtrées
  const filteredPermissions = permissions?.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchModule = filterModule === "all" || p.module === filterModule;
    return matchSearch && matchModule;
  });

  // Nombre total de permissions du rôle
  const totalPermissions = selectedRole?.permissions?.length ?? 0;

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      {/* En-tête */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-admin-text-primary md:text-3xl">
            Gestion des rôles et permissions
          </h1>
          <p className="mt-1 text-sm text-admin-text-muted">
            Gérez les rôles système et personnalisés, ainsi que leurs permissions
          </p>
        </div>
        <Button
          onClick={() => roleDialog.open("create", { id: "role-form", item: {} })}
          className="w-full bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90 sm:w-auto"
        >
          <PlusIcon size={18} className="mr-2" />
          Nouveau rôle
        </Button>
      </div>

      {/* Grille principale */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
        {/* Colonne de gauche : Liste des rôles */}
        <div className="lg:col-span-1">
          <Card className="border-admin-border bg-admin-surface">
            <CardHeader className="border-b border-admin-border pb-3">
              <CardTitle className="text-base font-semibold text-admin-text-primary">
                Rôles ({roles?.length ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[320px] sm:h-[450px] lg:h-[600px]">
                {rolesLoading ? (
                  <div className="p-4 text-center text-admin-text-muted">
                    <SpinnerIcon className="mr-2 inline-block animate-spin" size={16} />
                    Chargement des rôles...
                  </div>
                ) : roles?.length === 0 ? (
                  <div className="p-4 text-center text-admin-text-muted">Aucun rôle trouvé</div>
                ) : (
                  <div className="divide-y divide-admin-border">
                    {roles?.map((role) => (
                      <div
                        key={role.id}
                        className={cn(
                          "cursor-pointer p-4 transition-colors hover:bg-admin-surface-hover",
                          selectedRoleId === role.id &&
                            "border-l-4 border-admin-primary bg-admin-primary/10",
                        )}
                        onClick={() => setSelectedRoleId(role.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ShieldIcon size={16} className="text-admin-text-secondary" />
                            <span className="font-medium text-admin-text-primary">{role.name}</span>
                          </div>
                          {role.isSystem && (
                            <Badge variant="info" className="text-xs">
                              Système
                            </Badge>
                          )}
                        </div>
                        {role.description && (
                          <p className="mt-1 truncate text-xs text-admin-text-muted">
                            {role.description}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-4 text-xs text-admin-text-muted">
                          <span>{role.permissions?.length ?? 0} permissions</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <UsersIcon size={12} />0 utilisateurs
                          </span>
                        </div>

                        {/* Actions rapides (hors système) */}
                        {!role.isSystem && (
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                roleDialog.open("update", {
                                  id: "role-form",
                                  item: { roleId: role.id },
                                });
                              }}
                              className="h-6 px-2 text-xs text-admin-text-secondary hover:text-admin-text-primary"
                              disabled={deleting}
                            >
                              <PencilIcon size={12} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRole(role.id, role.name);
                              }}
                              className="h-6 px-2 text-xs text-admin-text-secondary hover:text-admin-danger"
                              disabled={deleting}
                            >
                              <TrashIcon size={12} />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Colonne de droite : Détails du rôle sélectionné */}
        <div className="lg:col-span-2">
          {selectedRole ? (
            <Card className="border-admin-border bg-admin-surface">
              <CardHeader className="border-b border-admin-border pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-admin-text-primary">
                      {selectedRole.name}
                      {selectedRole.isSystem && (
                        <Badge variant="info" className="ml-2 text-xs">
                          Système
                        </Badge>
                      )}
                    </CardTitle>
                    {selectedRole.description && (
                      <p className="mt-1 text-sm text-admin-text-muted">
                        {selectedRole.description}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-admin-text-muted">
                    {totalPermissions} permissions
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {/* Filtres */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
                      size={16}
                    />
                    <Input
                      placeholder="Rechercher une permission..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-admin-background pl-9 text-admin-text-primary"
                    />
                  </div>
                  <Select value={filterModule} onValueChange={setFilterModule}>
                    <SelectTrigger className="w-full bg-admin-background sm:w-48">
                      <SelectValue placeholder="Tous les modules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les modules</SelectItem>
                      {modules.map((module) => (
                        <SelectItem key={module} value={module!}>
                          {module}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Liste des permissions */}
                <ScrollArea className="h-[300px] sm:h-[380px] lg:h-[450px]">
                  {permissionsLoading ? (
                    <div className="p-4 text-center text-admin-text-muted">
                      <SpinnerIcon className="mr-2 inline-block animate-spin" size={16} />
                      Chargement des permissions...
                    </div>
                  ) : filteredPermissions?.length === 0 ? (
                    <div className="p-4 text-center text-admin-text-muted">
                      {searchTerm || filterModule !== "all"
                        ? "Aucune permission ne correspond à vos filtres"
                        : "Aucune permission disponible"}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {filteredPermissions?.map((permission) => {
                        const assigned = isPermissionAssigned(permission.id);
                        const isTogglingThis = isToggling === permission.id;
                        const isDisabled =
                          selectedRole.isSystem || assigning || (isTogglingThis && !assigned);

                        return (
                          <div
                            key={permission.id}
                            className={cn(
                              "flex items-center justify-between rounded-md border p-3 transition-colors",
                              assigned
                                ? "border-admin-primary/30 bg-admin-primary/5"
                                : "border-admin-border hover:bg-admin-surface-hover",
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-medium text-admin-text-primary">
                                  {permission.name}
                                </span>
                              </div>
                              {permission.description && (
                                <p className="truncate text-xs text-admin-text-muted">
                                  {permission.description}
                                </p>
                              )}
                              {permission.module && (
                                <Badge className="mt-1 border-0 bg-admin-surface-hover text-xs text-admin-text-secondary">
                                  {permission.module}
                                </Badge>
                              )}
                            </div>
                            {!selectedRole.isSystem ? (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={assigned}
                                  onCheckedChange={() =>
                                    handleTogglePermission(permission.id, assigned)
                                  }
                                  disabled={isDisabled || isTogglingThis}
                                  className={cn(
                                    assigned ? "data-[state=checked]:bg-admin-primary" : "",
                                    isTogglingThis && "cursor-wait opacity-50",
                                  )}
                                />
                                {isTogglingThis && (
                                  <SpinnerIcon
                                    className="animate-spin text-admin-text-muted"
                                    size={14}
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-admin-text-muted">
                                <LockIcon
                                  size={16}
                                  className={
                                    assigned ? "text-admin-success" : "text-admin-text-disabled"
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>

                {/* Résumé des permissions sélectionnées */}
                {!selectedRole.isSystem && (
                  <div className="mt-4 border-t border-admin-border pt-4">
                    <div className="flex flex-col gap-1 text-sm text-admin-text-muted sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        {localPermissions.length} permissions sélectionnées sur{" "}
                        {permissions?.length ?? 0}
                      </span>
                      <span className="text-xs">
                        {Math.round((localPermissions.length / (permissions?.length ?? 1)) * 100)}%
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-admin-border bg-admin-surface">
              <CardContent className="p-8 text-center text-admin-text-muted md:p-12">
                <ShieldIcon size={48} className="mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">Sélectionnez un rôle</p>
                <p className="text-sm">
                  Choisissez un rôle dans la liste de gauche pour gérer ses permissions
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialogue de création/édition de rôle */}
      <RoleFormDialog
        open={roleDialog.isOpen}
        onClose={roleDialog.close}
        mode={roleDialog.mode === "create" ? "create" : "update"}
        initialData={roleDialog.mode === "update" ? selectedRole : undefined}
        onSubmit={handleRoleSubmit}
        loading={creating || updating}
      />
    </div>
  );
};

// Composant du formulaire de rôle (dialogue)
interface RoleFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "update";
  initialData?: Role;
  onSubmit: (data: RoleFormValues, mode: "create" | "update") => void;
  loading?: boolean;
}

const RoleFormDialog = ({
  open,
  onClose,
  mode,
  initialData,
  onSubmit,
  loading = false,
}: RoleFormDialogProps) => {
  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
    },
  });

  const handleSubmit = (data: RoleFormValues) => {
    onSubmit(data, mode);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Créer un rôle" : "Modifier le rôle"}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Créez un nouveau rôle personnalisé. Vous pourrez ensuite lui assigner des permissions."
              : "Modifiez les informations du rôle."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du rôle</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="ex: super-admin"
                      className="bg-admin-background"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optionnelle)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder="Description du rôle"
                      className="bg-admin-background"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-admin-primary text-admin-primary-foreground"
                disabled={loading}
              >
                {mode === "create" ? "Créer" : "Mettre à jour"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
